import { create } from 'zustand';
import { setModerationLists } from '@/utils/crisis';

// MWBI-666 — fetches the crisis / inappropriate word lists from Strapi
// (single-type: content-moderation) and pushes them into the detection
// module so admins can edit lists in the Strapi admin UI without a code
// redeploy.
//
// Fail-open: on any error the baked-in defaults in utils/crisis.ts stay
// active. A Strapi outage must never silently disable safety detection.

interface ContentModerationResponse {
  data?: {
    crisisKeywordsEn?: string | null;
    crisisKeywordsEs?: string | null;
    inappropriateWordsEn?: string | null;
    inappropriateWordsEs?: string | null;
    // MWBI-883 — three new hard-block categories + the false-positive allowlist.
    hateSpeechEn?: string | null;
    hateSpeechEs?: string | null;
    cyberbullyingEn?: string | null;
    cyberbullyingEs?: string | null;
    graphicContentEn?: string | null;
    graphicContentEs?: string | null;
    allowlistEn?: string | null;
    allowlistEs?: string | null;
  } | null;
}

interface ModerationStore {
  loaded: boolean;
  loading: boolean;
  error: string | null;
  hydrate: () => Promise<void>;
}

const baseUrl = '/api/strapi';

// Parse a textarea-style value (one entry per line) into a clean list:
// trim every line, drop empties, lowercase, dedupe. Lowercasing here keeps
// detection consistent with the existing case-insensitive matchers in
// utils/crisis.ts.
export function parseModerationList(value: string | null | undefined): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of value.split('\n')) {
    const trimmed = raw.trim().toLowerCase();
    if (!trimmed) continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

export const useModerationStore = create<ModerationStore>((set, get) => ({
  loaded: false,
  loading: false,
  error: null,

  hydrate: async () => {
    if (get().loaded || get().loading) return;
    set({ loading: true, error: null });
    try {
      const res = await fetch(`${baseUrl}/content-moderation`);
      if (!res.ok) {
        throw new Error(`content-moderation fetch failed: ${res.status} ${res.statusText}`);
      }
      const json = (await res.json()) as ContentModerationResponse;
      const data = json.data ?? {};

      // Merge the En + Es field for a category and dedupe across languages —
      // the same word can appear on both sides if an admin pasted it twice.
      const merge = (en?: string | null, es?: string | null): string[] =>
        Array.from(new Set([...parseModerationList(en), ...parseModerationList(es)]));

      // Empty-list guard: setModerationLists ignores empty lists, so defaults
      // stay active for any category the admin hasn't filled in (fail-open).
      setModerationLists({
        crisis: merge(data.crisisKeywordsEn, data.crisisKeywordsEs),
        inappropriate: merge(data.inappropriateWordsEn, data.inappropriateWordsEs),
        hateSpeech: merge(data.hateSpeechEn, data.hateSpeechEs),
        cyberbullying: merge(data.cyberbullyingEn, data.cyberbullyingEs),
        graphicContent: merge(data.graphicContentEn, data.graphicContentEs),
        allowlist: merge(data.allowlistEn, data.allowlistEs),
      });

      set({ loaded: true, loading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown error';
      // Intentionally do not throw — defaults remain active.
      console.warn('[moderationStore] hydrate failed, using baked-in defaults:', message);
      set({ loaded: true, loading: false, error: message });
    }
  },
}));
